import SubLocationCard from 'components/general/SubLocationCard';
import { Formik } from 'formik';
import { get, has, isEmpty, isNull } from 'lodash';
import * as Yup from 'yup';
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Switch,
  TextField,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  FormControl,
} from '@material-ui/core';

import Form from 'components/ui/Form';
import Button from 'components/ui/Button';
import Dialog from 'components/ui/Dialog';
import AddressFormFields from 'components/general/AddressFormFields';
import {
  StyledField,
  StyledHelperText,
  FormSectionTitle,
  GridContainer,
} from 'styles/form';

import PhoneNumber from 'components/ui/PhoneNumber';
import { useSnackBar } from 'context/SnackBarContext';
import {
  createSubLocation,
  deleteSubLocation,
  updateSubLocation,
} from 'api/sub-location';

const Component = styled.div`
  height: 100%;
`;

const AddSubLocationButton = styled(Button)`
  margin-bottom: 2em;
`;

const RoomField = styled.div`
  display: flex;
  flex-direction: column;
`;

const OrganizationSubLocations = ({
  subLocations,
  organizationId,
  fetchOrganization,
}) => {
  const [subLocationModalOpen, setSubLocationModalOpen] = useState();
  const [subLocation, setSubLocation] = useState({});

  const { id: subLocationId } = subLocation;

  const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

  const handleModalOpen = () => {
    setSubLocationModalOpen(true);
  };

  const handleModalClose = () => {
    setSubLocationModalOpen(false);
    setSubLocation({});
  };

  const handleDelete = async () => {
    try {
      await deleteSubLocation(subLocationId);
      setMessage('Sub location deleted');
      setSeverity('success');
    } catch (error) {
      console.error(error);
      setMessage('Something went wrong. Failed to delete sub location.');
      setSeverity('error');
    }

    fetchOrganization();
    setOpenSnackBar(true);
    handleModalClose();
  };

  const handleSave = async (values) => {
    if (organizationId) {
      if (!isEmpty(subLocation)) {
        try {
          await updateSubLocation(subLocationId, values, organizationId);

          fetchOrganization();
          setMessage('Saved changes');
          setSeverity('success');
        } catch (error) {
          console.error(error);
          setMessage('Something went wrong. Failed to save changes.');
          setSeverity('error');
        }
      } else {
        try {
          await createSubLocation(values, organizationId);

          fetchOrganization();
          setMessage('Sub location created');
          setSeverity('success');
        } catch (error) {
          console.error(error);
          setMessage('Something went wrong. Failed to create sub location.');
          setSeverity('error');
        }
      }
    } else {
      setMessage('Something went wrong. Failed to create sub location.');
      setSeverity('error');
    }

    setOpenSnackBar(true);
    handleModalClose();
  };

  return (
    <Component>
      <Formik
        initialValues={{
          isVirtual: subLocation.is_virtual || 0,
          nickName: subLocation.nick_name || '',
          room: subLocation.room || '',
          address: get(subLocation, 'address', ''),
          city: get(subLocation, 'city', ''),
          state: get(subLocation, 'state', ''),
          postalCode: get(subLocation, 'postal_code', ''),
          country: get(subLocation, 'country', ''),
          contactFirstName: subLocation.contact_first_name || '',
          contactLastName: subLocation.contact_last_name || '',
          contactTitle: subLocation.contact_title || '',
          contactEmail: subLocation.contact_email || '',
          contactPhone: subLocation.contact_phone || '',
        }}
        validationSchema={Yup.object().shape({
          isVirtual: Yup.number(),
          nickName: Yup.string(),
          room: Yup.string().when('isVirtual', {
            is: 1,
            then: Yup.string().nullable(),
            otherwise: Yup.string().required('field is required'),
          }),
          address: Yup.string().when('isVirtual', {
            is: 1,
            then: Yup.string().nullable(),
            otherwise: Yup.string().required('field is required'),
          }),
          city: Yup.string().when('isVirtual', {
            is: 1,
            then: Yup.string().nullable(),
            otherwise: Yup.string().required('field is required'),
          }),
          state: Yup.string().when('isVirtual', {
            is: 1,
            then: Yup.string().nullable(),
            otherwise: Yup.string().required('field is required'),
          }),
          postalCode: Yup.string().when('isVirtual', {
            is: 1,
            then: Yup.string().nullable(),
            otherwise: Yup.string().required('field is required'),
          }),
          country: Yup.string().when('isVirtual', {
            is: 1,
            then: Yup.string().nullable(),
            otherwise: Yup.string().required('field is required'),
          }),
          contactFirstName: Yup.string(),
          contactLastName: Yup.string(),
          contactTitle: Yup.string(),
          contactEmail: Yup.string(),
          contactPhone: Yup.string(),
        })}
        enableReinitialize
        onSubmit={handleSave}
      >
        {({
          values,
          errors,
          setFieldValue,
          handleChange,
          submitForm,
          touched,
          resetForm,
        }) => {
          const {
            nickName,
            room,
            address,
            city,
            state,
            postalCode,
            country,
            contactFirstName,
            contactLastName,
            contactTitle,
            contactEmail,
            contactPhone,
            isVirtual,
          } = values;

          return (
            <Dialog
              open={subLocationModalOpen}
              handleClose={handleModalClose}
              handleDelete={handleDelete}
              disableTopPadding
              maxWidth="sm"
              typeText="Sub Location"
              submitForm={async () => {
                await submitForm();
                await resetForm();
              }}
              disableDelete={isEmpty(subLocation)}
            >
              <FormSectionTitle>Sub Location</FormSectionTitle>
              <Form fullWidth onSubmit={submitForm}>
                <GridContainer>
                  <TextField
                    color="primary"
                    error={touched.nickName && has(errors, 'nickName')}
                    helperText={touched.nickName && errors.nickName}
                    label="Name"
                    name="nickName"
                    onChange={handleChange}
                    value={nickName}
                    variant="outlined"
                  />

                  <RoomField>
                    <StyledField variant="outlined">
                      <InputLabel required={isVirtual === 1}>
                        Room Name
                      </InputLabel>
                      <OutlinedInput
                        onChange={handleChange}
                        labelWidth={105}
                        error={touched.room && has(errors, 'room')}
                        helperText={touched.room && errors.room}
                        name="room"
                        value={room}
                      />
                    </StyledField>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={isVirtual === 1}
                          name="isVirtual"
                          onChange={(e) => {
                            setFieldValue(
                              'isVirtual',
                              e.target.checked ? 1 : 0,
                            );
                          }}
                          color="primary"
                          value={isVirtual}
                        />
                      }
                      label="Virtual Location"
                    />
                  </RoomField>

                  {!isVirtual && (
                    <AddressFormFields
                      address={address}
                      country={country}
                      city={city}
                      state={state}
                      postalCode={postalCode}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      fieldsRequired
                    />
                  )}

                  <TextField
                    color="primary"
                    error={
                      touched.contactFirstName &&
                      has(errors, 'contactFirstName')
                    }
                    fullWidth
                    helperText={
                      touched.contactFirstName && errors.contactFirstName
                    }
                    label="Contact First Name"
                    name="contactFirstName"
                    onChange={handleChange}
                    value={contactFirstName}
                    variant="outlined"
                  />

                  <TextField
                    color="primary"
                    error={
                      touched.contactLastName && has(errors, 'contactLastName')
                    }
                    fullWidth
                    helperText={
                      touched.contactLastName && errors.contactLastName
                    }
                    label="Contact Last Name"
                    name="contactLastName"
                    onChange={handleChange}
                    value={contactLastName}
                    variant="outlined"
                  />

                  <TextField
                    color="primary"
                    error={touched.contactTitle && has(errors, 'contactTitle')}
                    helperText={touched.contactTitle && errors.contactTitle}
                    label="Contact Title"
                    name="contactTitle"
                    onChange={handleChange}
                    value={contactTitle}
                    type="phone"
                    variant="outlined"
                  />

                  <TextField
                    color="primary"
                    error={touched.contactEmail && has(errors, 'contactEmail')}
                    helperText={touched.contactEmail && errors.contactEmail}
                    label="Contact Email"
                    name="contactEmail"
                    onChange={handleChange}
                    value={contactEmail}
                    variant="outlined"
                  />

                  <PhoneNumber
                    label="Contact Phone Number"
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    value={contactPhone}
                    name="contactPhone"
                  />
                </GridContainer>
              </Form>
            </Dialog>
          );
        }}
      </Formik>

      <AddSubLocationButton onClick={handleModalOpen}>
        Add Sub Location
      </AddSubLocationButton>

      <FormSectionTitle>Sub Locations</FormSectionTitle>

      {subLocations &&
        subLocations.map((location) => (
          <SubLocationCard
            subLocation={location}
            setSubLocation={setSubLocation}
            handleModalOpen={handleModalOpen}
          />
        ))}
    </Component>
  );
};

export default OrganizationSubLocations;
